import { useEffect, useRef, useState } from "react";
import { Pin, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button, inputClass } from "@/components/portal/Ui";

/** One pinned reminder per campaign, edited in place. */
export function StickyNote({ campaign, onSaved, compact = false }) {
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(campaign.sticky_note?.body || "");
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    setBody(campaign.sticky_note?.body || "");
  }, [campaign.sticky_note]);

  useEffect(() => {
    if (editing) ref.current?.focus();
  }, [editing]);

  const save = async (next) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.put(`/campaigns/${campaign.id}/note`, { body: next });
      toast.success(next.trim() ? "Sticky note saved" : "Sticky note cleared");
      setEditing(false);
      await onSaved();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <div className="w-full min-w-[190px]" data-testid={`sticky-editor-${campaign.id}`}>
        <textarea
          ref={ref}
          rows={compact ? 2 : 3}
          maxLength={500}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") setEditing(false);
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) save(body);
          }}
          placeholder="e.g. Client wants captions in BM. Invoices due 30 Sep."
          className={`${inputClass} bg-[#FFF8E6] text-[13px]`}
          data-testid={`sticky-input-${campaign.id}`}
        />
        <div className="mt-1.5 flex items-center gap-2">
          <Button busy={busy} onClick={() => save(body)} testId={`sticky-save-${campaign.id}`}>
            Save note
          </Button>
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="text-xs font-semibold body-text hover:text-[#111111]"
            data-testid={`sticky-cancel-${campaign.id}`}
          >
            Cancel
          </button>
          {campaign.sticky_note && (
            <button
              type="button"
              onClick={() => save("")}
              className="ml-auto text-xs font-semibold text-[#B3261E] hover:underline"
              data-testid={`sticky-clear-${campaign.id}`}
            >
              Remove
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!campaign.sticky_note) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-[#DFD8CB] px-2.5 py-1.5 text-xs font-semibold body-text transition-colors hover:border-[#F2811D] hover:text-[#F2811D]"
        data-testid={`sticky-add-${campaign.id}`}
      >
        <Pin className="h-3.5 w-3.5" />
        Add note
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      title={`${campaign.sticky_note.body}\n— ${campaign.sticky_note.author_name}`}
      className="group block w-full min-w-[170px] max-w-[280px] rounded-lg border border-[#F2D8A0] bg-[#FFF8E6] px-2.5 py-2 text-left transition-colors hover:border-[#F2811D]"
      data-testid={`sticky-note-${campaign.id}`}
    >
      <p className={`text-[13px] font-semibold leading-snug ${compact ? "line-clamp-2" : ""}`}>
        {campaign.sticky_note.body}
      </p>
      <p className="mt-1 text-[11px] body-text">— {campaign.sticky_note.author_name}</p>
    </button>
  );
}

export function FilterChip({ active, count, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-bold transition-colors ${
        active
          ? "border-[#111111] bg-[#111111] text-white"
          : "border-[#E4DED3] bg-white body-text hover:border-[#111111] hover:text-[#111111]"
      }`}
    >
      {children}
      <span className={`tabular ${active ? "text-white/70" : "text-[#8a8378]"}`}>{count}</span>
    </button>
  );
}

export function ClearFilters({ onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 text-xs font-semibold text-[#B3261E] hover:underline"
      data-testid={testId}
    >
      <X className="h-3.5 w-3.5" />
      Clear filters
    </button>
  );
}
